export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-6">
              This Privacy Policy describes how OpenApps, Inc. ("OpenApps," "we," "our," or "us") collects, uses, 
              and shares personal information in connection with our services, website, and applications.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information You Provide to Us</h3>
            <p className="text-gray-700 mb-4">
              We collect information you provide directly to us, such as when you create an account, 
              use our services, or communicate with us. This includes:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Account Information:</strong> Email address, name, password, and other registration details</li>
              <li><strong>Profile Information:</strong> Optional profile details, preferences, and settings</li>
              <li><strong>API Specifications:</strong> OpenAPI documents, configuration files, and related technical data</li>
              <li><strong>Payment Information:</strong> Billing details and payment method information (processed by third-party providers)</li>
              <li><strong>Communications:</strong> Messages, support tickets, feedback, and correspondence with our team</li>
              <li><strong>User Content:</strong> Any data, files, or content you upload, create, or store using our services</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information We Collect Automatically</h3>
            <p className="text-gray-700 mb-4">
              When you access or use our services, we automatically collect certain information:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Usage Data:</strong> Information about how you use our services, including features accessed, 
                  time spent, and performance metrics</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type and version, 
                  IP address, and unique device identifiers</li>
              <li><strong>Log Data:</strong> Server logs, error reports, and system activity information</li>
              <li><strong>Analytics Data:</strong> Performance metrics, usage patterns, and service optimization data</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information from Third Parties</h3>
            <p className="text-gray-700 mb-6">
              We may receive information about you from third parties, such as authentication providers, 
              payment processors, or integration partners, in accordance with their privacy policies.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Provide, maintain, and improve our services and platform functionality</li>
              <li>Process your transactions and manage your account</li>
              <li>Authenticate users and ensure platform security</li>
              <li>Generate MCP servers and manage API integrations</li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Send you technical notices, updates, security alerts, and administrative messages</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Develop new features and services</li>
              <li>Comply with legal obligations and enforce our terms of service</li>
              <li>Protect against fraud, abuse, and security threats</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How We Share Your Information</h2>
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties, 
              except in the following circumstances:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Service Providers</h3>
            <p className="text-gray-700 mb-4">
              We may share your information with third-party service providers who perform services on our behalf, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Cloud hosting and infrastructure providers</li>
              <li>Payment processing services</li>
              <li>Analytics and monitoring services</li>
              <li>Customer support platforms</li>
              <li>Security and fraud prevention services</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Legal Requirements</h3>
            <p className="text-gray-700 mb-6">
              We may disclose your information if required to do so by law or in response to valid requests 
              by public authorities, including to meet national security or law enforcement requirements.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Business Transfers</h3>
            <p className="text-gray-700 mb-6">
              In the event of a merger, acquisition, or sale of all or part of our business, 
              your information may be transferred as part of that transaction.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. These measures include encryption 
              of data in transit and at rest, access controls, regular security assessments, and employee training. 
              However, no method of transmission over the internet or electronic storage is 100% secure.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">International Data Transfers</h2>
            <p className="text-gray-700 mb-6">
              Your information may be processed and stored in countries other than your own, including the United States. 
              When we transfer personal information internationally, we ensure appropriate safeguards are in place 
              to protect your data in accordance with applicable data protection laws.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Data Retention</h2>
            <p className="text-gray-700 mb-6">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
              Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer 
              need your information, we will securely delete or anonymize it.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your Rights and Choices</h2>
            <p className="text-gray-700 mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Restriction:</strong> Request limitation of how we process your information</li>
              <li><strong>Objection:</strong> Object to our processing of your information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar tracking technologies to collect and track information and to improve our services. 
              You can control cookies through your browser settings, but disabling cookies may affect the functionality 
              of our services.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 mb-6">
              Our services may contain links to third-party websites or services. We are not responsible for the 
              privacy practices of these third parties, and we encourage you to review their privacy policies.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 mb-6">
              Our services are not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we become aware that we have collected such 
              information, we will delete it promptly.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on our website and updating the "Last updated" date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Regional Privacy Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">European Union and United Kingdom</h3>
            <p className="text-gray-700 mb-4">
              If you are located in the EU or UK, you have additional rights under the General Data Protection 
              Regulation (GDPR) and UK GDPR, including the right to lodge a complaint with a supervisory authority.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">California</h3>
            <p className="text-gray-700 mb-6">
              California residents have additional rights under the California Consumer Privacy Act (CCPA), 
              including the right to know what personal information we collect and how we use it, and the right 
              to request deletion of personal information.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-2">
                <strong>OpenApps, Inc.</strong><br/>
                Email: <a href="mailto:privacy@openapps.ai" className="text-blue-600 hover:text-blue-800">privacy@openapps.ai</a><br/>
                Data Protection Officer: <a href="mailto:dpo@openapps.ai" className="text-blue-600 hover:text-blue-800">dpo@openapps.ai</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-6">
              By accessing or using OpenApps, you agree to be bound by these Terms of Service and our Privacy Policy. 
              If you do not agree to these terms, do not use our service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              OpenApps provides a platform for converting OpenAPI specifications into Model Context Protocol (MCP) servers. Our services include:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Automatic OpenAPI to MCP server conversion</li>
              <li>Cloud hosting and deployment of MCP servers</li>
              <li>Real-time analytics and monitoring</li>
              <li>Team collaboration and access management</li>
              <li>API integration and webhook support</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
            <p className="text-gray-700 mb-4">
              To use OpenApps, you must create an account and provide accurate information. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Promptly notifying us of any unauthorized use</li>
              <li>Ensuring your contact information is current</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to use OpenApps to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the service for illegal or harmful activities</li>
              <li>Generate excessive load that impacts service performance</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Content and Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              You retain ownership of your OpenAPI specifications and generated content. By using OpenApps, you grant us:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>License to process your OpenAPI specs to provide our services</li>
              <li>Right to store and backup your data for service operation</li>
              <li>Permission to analyze usage patterns for service improvement</li>
            </ul>
            <p className="text-gray-700 mb-6">
              OpenApps retains all rights to our platform, technology, and proprietary algorithms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Payment and Billing</h2>
            <p className="text-gray-700 mb-4">
              Paid plans are billed in advance on a monthly basis. By subscribing, you agree to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Provide accurate billing information</li>
              <li>Pay all fees and charges on time</li>
              <li>Notify us of billing changes promptly</li>
              <li>Accept automatic renewal unless cancelled</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Service Availability</h2>
            <p className="text-gray-700 mb-6">
              We strive to maintain high availability but do not guarantee uninterrupted service. 
              We may perform maintenance, updates, or modifications that temporarily affect service availability.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              OpenApps is provided "as is" without warranties. We are not liable for indirect, 
              incidental, or consequential damages arising from your use of our service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Termination</h2>
            <p className="text-gray-700 mb-6">
              Either party may terminate this agreement at any time. Upon termination, 
              your access to the service will cease and your data may be deleted according to our retention policy.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We may update these terms periodically. Continued use of OpenApps after changes 
              constitutes acceptance of the new terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Contact Information</h2>
            <p className="text-gray-700">
              For questions about these terms, contact us at:<br/>
              Email: <a href="mailto:legal@openapps.ai" className="text-blue-600 hover:text-blue-800">legal@openapps.ai</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function CookiePage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Cookie Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">What Are Cookies</h2>
            <p className="text-gray-700 mb-6">
              Cookies are small text files stored on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and improving our service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 mb-4">OpenApps uses cookies for the following purposes:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Essential Cookies</h3>
            <p className="text-gray-700 mb-4">These cookies are necessary for the website to function properly:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Authentication:</strong> To keep you logged in and secure your session</li>
              <li><strong>Security:</strong> To protect against cross-site request forgery and other attacks</li>
              <li><strong>Load Balancing:</strong> To ensure optimal server performance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Analytics Cookies</h3>
            <p className="text-gray-700 mb-4">We use these to understand how you use our platform:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Usage Analytics:</strong> To track page views and user interactions</li>
              <li><strong>Performance Monitoring:</strong> To identify and fix technical issues</li>
              <li><strong>Feature Usage:</strong> To understand which features are most valuable</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Preference Cookies</h3>
            <p className="text-gray-700 mb-4">These remember your choices to improve your experience:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Theme Settings:</strong> To remember your dark/light mode preference</li>
              <li><strong>Dashboard Layout:</strong> To save your custom dashboard configuration</li>
              <li><strong>Language:</strong> To display content in your preferred language</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We may use third-party services that set their own cookies:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Authentication Providers:</strong> For secure login and user management</li>
              <li><strong>Analytics Services:</strong> To measure website performance and usage</li>
              <li><strong>Support Chat:</strong> To provide customer support functionality</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Cookie Retention</h2>
            <p className="text-gray-700 mb-4">Different cookies have different retention periods:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Stored for up to 1 year</li>
              <li><strong>Authentication Cookies:</strong> Valid for 30 days or until logout</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Managing Cookies</h2>
            <p className="text-gray-700 mb-4">You can control cookies through:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies</li>
              <li><strong>Privacy Settings:</strong> Use your browser's privacy/incognito mode</li>
              <li><strong>Third-Party Tools:</strong> Browser extensions for cookie management</li>
            </ul>
            <p className="text-gray-700 mb-6">
              <strong>Note:</strong> Disabling certain cookies may affect the functionality of OpenApps, 
              particularly authentication and personalization features.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Cookie Consent</h2>
            <p className="text-gray-700 mb-6">
              By continuing to use OpenApps, you consent to our use of cookies as described in this policy. 
              You can withdraw consent at any time by adjusting your browser settings.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Cookie Policy from time to time. Any changes will be posted on this page 
              with an updated revision date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about our use of cookies, please contact us at:<br/>
              Email: <a href="mailto:privacy@openapps.ai" className="text-blue-600 hover:text-blue-800">privacy@openapps.ai</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}


